import * as React from "react";
import styled from "styled-components";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  ContentContainer,
  Card,
  InfoBox,
  TipCard,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

const e1Bild = require("../../assets/e1.jpg");

// Seitenspezifische Styled Components

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 24px 0;
`;

const StatBox = styled.div`
  background: linear-gradient(135deg, var(--primaryColor) 0%, #1976d2 100%);
  color: white;
  padding: 20px 24px;
  border-radius: 12px;
  text-align: center;

  .stat-number {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 13px;
    opacity: 0.9;
  }
`;

const CalloutBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%);
  border-left: 4px solid #5c6bc0;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "HINWEIS";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #5c6bc0;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #283593;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

// Text mit Absätzen und Aufzählungslisten rendern
const renderText = (text: string) => {
  if (!text) return null;
  return text.split("\n\n").map((paragraph, index) => {
    if (paragraph.includes("\n\u2022") || paragraph.startsWith("\u2022")) {
      const lines = paragraph.split("\n");
      const intro = lines[0].startsWith("\u2022") ? null : lines[0];
      const items = lines.filter((item) => item.trim().startsWith("\u2022"));
      return (
        <div key={index}>
          {intro && <p>{processInlineFormatting(intro)}</p>}
          <ul>
            {items.map((item, i) => (
              <li key={i}>
                {processInlineFormatting(item.replace(/^\u2022\s*/, ""))}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return <p key={index}>{processInlineFormatting(paragraph)}</p>;
  });
};

export const AbwasserDetailRoute = React.memo(function AbwasserDetailRoute() {
  const { t, getKeysWithPrefix } = useTexts();

  // Dynamisch alle Literatur-Nummern finden
  const literaturNumbers = React.useMemo(() => {
    const textKeys = getKeysWithPrefix("E.1.literatur.");
    const numbers = new Set<number>();
    textKeys.forEach((key) => {
      const match = key.match(/^E\.1\.literatur\.(\d+)\./);
      if (match) {
        numbers.add(parseInt(match[1], 10));
      }
    });
    return Array.from(numbers).sort((a, b) => a - b);
  }, [getKeysWithPrefix]);

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("E.1.title", "Abwasser")}</h1>

        {/* Definition */}
        <Card>
          <h2>
            {t("E.1.definition.title", "Definition und Bedeutung von Abwasser")}
          </h2>
          {renderText(t("E.1.definition.text1", ""))}
          {renderText(t("E.1.definition.text2", ""))}
          {renderText(t("E.1.definition.text3", ""))}
        </Card>

        {/* Zahlen und Fakten */}
        <Card>
          <h2>
            {t("E.1.zahlen.title", "Zahlen und Fakten zur Abwasserentsorgung")}
          </h2>

          <StatGrid>
            <StatBox>
              <div className="stat-number">
                {t("E.1.zahlen.anschlussgrad", "96 %")}
              </div>
              <div className="stat-label">
                {t(
                  "E.1.zahlen.anschlussgrad.label",
                  "Anschlussgrad an Kläranlagen"
                )}
              </div>
            </StatBox>
            <StatBox>
              <div className="stat-number">
                {t("E.1.zahlen.klaerwerke", "8.891")}
              </div>
              <div className="stat-label">
                {t("E.1.zahlen.klaerwerke.label", "Anzahl Kläranlagen")}
              </div>
            </StatBox>
            <StatBox>
              <div className="stat-number">
                {t("E.1.zahlen.kanalnetz", "608.000 km")}
              </div>
              <div className="stat-label">
                {t("E.1.zahlen.kanalnetz.label", "Kanalnetzlänge")}
              </div>
            </StatBox>
            <StatBox>
              <div className="stat-number">
                {t("E.1.zahlen.unternehmen", "3.000")}
              </div>
              <div className="stat-label">
                {t(
                  "E.1.zahlen.unternehmen.label",
                  "Eigene Abwasserbehandlungsanlagen"
                )}
              </div>
            </StatBox>
          </StatGrid>

          {renderText(t("E.1.zahlen.text1", ""))}
          {renderText(t("E.1.zahlen.text2", ""))}
          {renderText(t("E.1.zahlen.text3", ""))}
        </Card>

        {/* Richtlinien */}
        <Card>
          <h2>{t("E.1.richtlinien.title", "Richtlinien und Regelungen")}</h2>

          <h3>{t("E.1.richtlinien.eu.title", "Der europäische Rahmen")}</h3>
          {renderText(t("E.1.richtlinien.eu.text", ""))}

          <CalloutBox>
            <h4>{t("E.1.karl.title", "Die Anforderungen der KARL")}</h4>
            <p>{t("E.1.karl.intro", "")}</p>
          </CalloutBox>

          <ol>
            <li>{processInlineFormatting(t("E.1.karl.punkt1", ""))}</li>
            <li>{processInlineFormatting(t("E.1.karl.punkt2", ""))}</li>
            <li>{processInlineFormatting(t("E.1.karl.punkt3", ""))}</li>
            <li>{processInlineFormatting(t("E.1.karl.punkt4", ""))}</li>
            <li>{processInlineFormatting(t("E.1.karl.punkt5", ""))}</li>
            <li>{processInlineFormatting(t("E.1.karl.punkt6", ""))}</li>
          </ol>

          {renderText(t("E.1.karl.inkrafttreten", ""))}

          <h3>{t("E.1.whg.title", "Der deutsche Rahmen: WHG und AbwV")}</h3>
          {renderText(t("E.1.whg.text1", ""))}
          {renderText(t("E.1.whg.text2", ""))}
          {renderText(t("E.1.whg.text3", ""))}
        </Card>

        {/* Geschichte */}
        <Card>
          <h2>
            {t(
              "E.1.geschichte.title",
              "Die Bedeutung des Abwasserleitungssystems"
            )}
          </h2>

          {renderText(t("E.1.geschichte.intro", ""))}

          <h3>{t("E.1.geschichte.roemer.subtitle", "Römische Zeit")}</h3>
          {renderText(t("E.1.geschichte.roemer", ""))}

          <h3>{t("E.1.geschichte.mittelalter.subtitle", "Mittelalter")}</h3>
          {renderText(t("E.1.geschichte.mittelalter", ""))}

          <CalloutBox>
            <h4>{t("E.1.geschichte.heute.subtitle", "Heute")}</h4>
            <p>{t("E.1.geschichte.heute", "")}</p>
          </CalloutBox>
        </Card>

        {/* Kläranlage */}
        <Card>
          <h2>
            {t("E.1.klaeranlage.title", "Funktionsweise einer Kläranlage")}
          </h2>
          {renderText(t("E.1.klaeranlage.intro", ""))}

          <ImageContainer>
            <img src={e1Bild} alt="Funktionsweise einer Kläranlage" />
            <FigureCaption>
              Abbildung 1:{" "}
              {t(
                "E.1.klaeranlage.bild.caption",
                "Funktionsweise einer Kläranlage (Quelle: Verbraucherzentrale NRW)"
              )}
            </FigureCaption>
          </ImageContainer>

          <h3>
            {t(
              "E.1.stufe1.title",
              "Erste Reinigungsstufe – Mechanische Reinigung"
            )}
          </h3>

          <h4>{t("E.1.stufe1.rechen.title", "Rechen")}</h4>
          {renderText(t("E.1.stufe1.rechen.text", ""))}

          <h4>{t("E.1.stufe1.sandfang.title", "Sand- und Fettfang")}</h4>
          {renderText(t("E.1.stufe1.sandfang.text", ""))}

          <h4>{t("E.1.stufe1.vorklaer.title", "Vorklärbecken")}</h4>
          {renderText(t("E.1.stufe1.vorklaer.text", ""))}

          <h3>
            {t(
              "E.1.stufe2.title",
              "Zweite Reinigungsstufe – Biologische Reinigung"
            )}
          </h3>

          <h4>{t("E.1.stufe2.belebung.title", "Belebungsbecken")}</h4>
          {renderText(t("E.1.stufe2.belebung.text", ""))}

          <h4>{t("E.1.stufe2.nachklaer.title", "Nachklärbecken")}</h4>
          {renderText(t("E.1.stufe2.nachklaer.text", ""))}

          <h3>
            {t(
              "E.1.stufe3.title",
              "Dritte Reinigungsstufe – Chemische Reinigung"
            )}
          </h3>

          <InfoBox>
            <h4>Phosphatelimination</h4>
            {renderText(t("E.1.stufe3.text1", ""))}
            {renderText(t("E.1.stufe3.text2", ""))}
            {renderText(t("E.1.stufe3.text3", ""))}
            {renderText(t("E.1.stufe3.text4", ""))}
          </InfoBox>

          <h4>
            {t("E.1.schlamm.title", "Schlammbehandlung und Energiegewinnung")}
          </h4>
          {renderText(t("E.1.schlamm.text1", ""))}
          {renderText(t("E.1.schlamm.text2", ""))}
          {renderText(t("E.1.schlamm.text3", ""))}
          {renderText(t("E.1.schlamm.text4", ""))}
        </Card>

        {/* Vierte Reinigungsstufe */}
        <Card>
          <h2>
            {t(
              "E.1.stufe4.title",
              "Vierte Reinigungsstufe – Entfernung von Spurenstoffen"
            )}
          </h2>
          {renderText(t("E.1.stufe4.intro", ""))}

          <InfoBox>
            <h4>{t("E.1.spurenstoffe.title", "Was sind Spurenstoffe?")}</h4>
            {renderText(t("E.1.spurenstoffe.text1", ""))}
            {renderText(t("E.1.spurenstoffe.text2", ""))}
            {renderText(t("E.1.spurenstoffe.text3", ""))}
          </InfoBox>

          <h3>{t("E.1.verfahren.title", "Technische Verfahren")}</h3>

          <h4>{t("E.1.verfahren.ozon.title", "1. Ozonung")}</h4>
          {renderText(t("E.1.verfahren.ozon.text", ""))}

          <h4>{t("E.1.verfahren.kohle.title", "2. Aktivkohleadsorption")}</h4>
          {renderText(t("E.1.verfahren.kohle.text", ""))}

          <h3>{t("E.1.karl.ausbau.title", "Vorgaben durch KARL")}</h3>
          {renderText(t("E.1.karl.ausbau.text", ""))}

          <h3>{t("E.1.umsetzung.title", "Stand der Umsetzung")}</h3>
          {renderText(t("E.1.umsetzung.text1", ""))}

          <CalloutBox>
            <h4>Kosten und Wirksamkeit</h4>
            {renderText(t("E.1.umsetzung.text2", ""))}
          </CalloutBox>

          <CalloutBox>
            <h4>Ergebnis</h4>
            <p>{t("E.1.umsetzung.erfolg", "")}</p>
          </CalloutBox>
        </Card>

        {/* Vermeiden statt Reinigen */}
        <Card>
          <h2>{t("E.1.vermeiden.title", "Vermeiden statt nur Reinigen")}</h2>
          {renderText(t("E.1.vermeiden.intro", ""))}

          <TipCard>
            <h4>Was du tun kannst</h4>
            {renderText(t("E.1.vermeiden.tipps", ""))}
          </TipCard>
        </Card>

        <HighlightBox>
          <p>{processInlineFormatting(t("E.1.vermeiden.fazit", ""))}</p>
        </HighlightBox>

        {/* Literatur */}
        <SourcesSection>
          <h3>{t("E.1.literatur.title", "Literatur")}</h3>
          <ol>
            {literaturNumbers.map((num) => {
              const text = t(`E.1.literatur.${num}.text`, "");
              const url = t(`E.1.literatur.${num}.url`, "");
              return (
                <li key={num} id={`quelle-${num}`}>
                  <LinkifyText>{url ? `${text} ${url}` : text}</LinkifyText>
                </li>
              );
            })}
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
